import React from 'react'
import { Depths } from '@uifabric/fluent-theme/lib/fluent/FluentDepths'
import {
  DetailsListLayoutMode,
  DetailsList,
  CheckboxVisibility,
  SelectionMode,
  Stack,
  Spinner,
  SpinnerSize,
  IColumn,
  Selection,
  IObjectWithKey,
  DefaultButton,
  TextField,
  IDetailsHeaderProps,
} from 'office-ui-fabric-react'
import './TableView.css'
import { Filter } from './Filter'

interface State<T> {
  cachedRows?: { cacheKey: string; data: T[] }
  sortedColumn?: {
    direction: 'ascending' | 'descending'
    columnKey: string
  }
  textSearch: string
  columnFilters: Record<string, string>
  // real on-screen widths of the header cells, measured from the DOM so the
  // filter row lines up exactly instead of guessing Fabric's internal widths
  measuredColumnWidths: Record<string, number>
  // width of whatever Fabric renders before the first data column (the
  // select-all checkbox cell when isSelectable) so the filter row can offset
  // by the same amount and stay aligned
  measuredLeadingOffset: number
}

interface InputProps<T> {
  columns: IColumn[]
  rows?: T[]
  onRowClick: (row: T, e: React.MouseEvent<HTMLElement>) => void
  rowToCacheKey: (row: T) => string
  postSort?: (a: T, b: T, rows: T[]) => number
  cacheKey: string
  isSelectable?: boolean
  onSelectionChanged?: (selection: IObjectWithKey[]) => void
  onCancel?: () => void
  large?: boolean
  onFilteredRowsChange?: (filteredRows: T[] | undefined) => void
  rowFilter?: (row: T) => boolean
  // column keys that get a text filter box rendered in their header
  filterableColumnKeys?: string[]
}

export class TableView<T> extends React.Component<InputProps<T>, State<T>> {
  private selection: Selection
  private textFilteredRows: T[] = []
  private headerContainer: HTMLDivElement | null = null
  private resizeObserver = new ResizeObserver(() => this.measureColumnWidths())
  constructor(params: any) {
    super(params)
    this.selection = new Selection({
      onSelectionChanged: () =>
        this.props.onSelectionChanged
          ? this.props.onSelectionChanged(this.selection.getSelection())
          : null,
    })
    this.state = {
      sortedColumn: undefined,
      textSearch: '',
      columnFilters: {},
      measuredColumnWidths: {},
      measuredLeadingOffset: 0,
    }
  }
  componentWillUnmount(): void {
    this.resizeObserver.disconnect()
  }
  // header mounts after rows load, later than componentDidMount
  setHeaderContainer = (el: HTMLDivElement | null) => {
    this.resizeObserver.disconnect()
    this.headerContainer = el
    if (el) {
      this.resizeObserver.observe(el)
      this.measureColumnWidths()
    }
  }
  componentDidUpdate(): void {
    if (!this.props.isSelectable) {
      this.selection.setAllSelected(false)
    }
  }
  // widths Fabric assigns to header cells depend on internal padding/border
  // constants that shift between versions/themes; measuring the real
  // rendered cells is the only way the filter row stays pixel-aligned
  measureColumnWidths = () => {
    const container = this.headerContainer
    if (!container || !(this.props.filterableColumnKeys || []).length) {
      return
    }
    const widths: Record<string, number> = {}
    const dataCells = container.querySelectorAll('[data-item-key]')
    dataCells.forEach((el: Element) => {
      const key = el.getAttribute('data-item-key')
      if (!key) {
        return
      }
      widths[key] = (el as HTMLElement).getBoundingClientRect().width
      // justified layout resizes cells without resizing the container
      this.resizeObserver.observe(el)
    })
    // anything Fabric renders before the first data column (e.g. the
    // select-all checkbox cell) needs a matching spacer in the filter row
    const firstDataCell = dataCells[0] as HTMLElement | undefined
    const leadingOffset = firstDataCell
      ? firstDataCell.getBoundingClientRect().left -
        container.getBoundingClientRect().left
      : 0
    const changed =
      Object.keys(widths).length !==
        Object.keys(this.state.measuredColumnWidths).length ||
      Object.keys(widths).some(
        (key) => widths[key] !== this.state.measuredColumnWidths[key]
      ) ||
      leadingOffset !== this.state.measuredLeadingOffset
    if (changed) {
      this.setState({
        measuredColumnWidths: widths,
        measuredLeadingOffset: leadingOffset,
      })
    }
  }
  render() {
    const rows = this.getSortedRows()
    if (!rows) {
      return this.renderLoading()
    }
    const activeColumnFilters = Object.entries(this.state.columnFilters).filter(
      ([, value]) => value
    )
    const textFilteredRows = rows.filter(
      (r) =>
        JSON.stringify(r)
          .replace(/"[^"]+":/g, '')
          .toLowerCase()
          .indexOf(this.state.textSearch.toLowerCase()) !== -1
    )
    const columnFilteredRows = textFilteredRows.filter((r) =>
      activeColumnFilters.every(([columnKey, value]) =>
        this.matchesColumnFilter(r, columnKey, value)
      )
    )
    this.textFilteredRows = columnFilteredRows
    const filteredRows = this.props.rowFilter
      ? columnFilteredRows.filter(this.props.rowFilter)
      : columnFilteredRows
    return (
      <React.Fragment>
        <div className="caroda-table-view">
          <DetailsList
            checkboxVisibility={
              this.props.isSelectable
                ? CheckboxVisibility.always
                : CheckboxVisibility.hidden
            }
            items={filteredRows}
            columns={this.getColumns()}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            enableUpdateAnimations={true}
            onShouldVirtualize={() => filteredRows.length > 100}
            selection={this.selection}
            selectionMode={
              this.props.isSelectable
                ? SelectionMode.multiple
                : SelectionMode.none
            }
            selectionPreservedOnEmptyClick={true}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="select row"
            onRenderDetailsHeader={(props, defaultRender) =>
              this.renderDetailsHeader(props, defaultRender)
            }
            styles={{ root: { boxShadow: Depths.depth4 } }}
            className={this.props.large ? 'large' : ''}
          />
          <div style={{ height: 64 }}></div>
        </div>
        {this.renderFilter()}
        {this.props.isSelectable && this.renderCancelButton()}
      </React.Fragment>
    )
  }
  matchesColumnFilter(row: T, columnKey: string, filterValue: string): boolean {
    const column = this.props.columns.find((c) => c.key === columnKey)
    if (!column || !column.fieldName) {
      return true
    }
    let field: any = (row as any)[column.fieldName]
    if (Array.isArray(field)) {
      field = field.join(',')
    }
    return String(field || '')
      .toLowerCase()
      .includes(filterValue.toLowerCase())
  }
  renderDetailsHeader(
    props?: IDetailsHeaderProps,
    defaultRender?: (props?: IDetailsHeaderProps) => JSX.Element | null
  ) {
    if (!props) {
      return null
    }
    const filterableColumnKeys = this.props.filterableColumnKeys || []
    if (!filterableColumnKeys.length || !defaultRender) {
      return defaultRender ? defaultRender(props) : null
    }
    return (
      <div ref={this.setHeaderContainer}>
        {defaultRender(props)}
        <div className="column-filter-row">
          {this.state.measuredLeadingOffset > 0 && (
            <div
              style={{
                width: this.state.measuredLeadingOffset,
                flexShrink: 0,
              }}
            />
          )}
          {(props.columns as IColumn[]).map((column) => (
            <div
              key={column.key}
              style={{
                // fall back to Fabric's own calculatedWidth before the first
                // real measurement lands, to avoid a 0-width flash
                width:
                  this.state.measuredColumnWidths[column.key] ||
                  column.calculatedWidth,
                flexShrink: 0,
                boxSizing: 'border-box',
                padding: '8px 8px 0',
              }}
            >
              {filterableColumnKeys.includes(column.key) && (
                <TextField
                  placeholder={`Filter ${column.name}`}
                  value={this.state.columnFilters[column.key] || ''}
                  onChange={(_, value) =>
                    this.setColumnFilter(column.key, value || '')
                  }
                />
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }
  setColumnFilter(columnKey: string, value: string) {
    this.setState(
      {
        columnFilters: { ...this.state.columnFilters, [columnKey]: value },
      },
      () => {
        if (this.props.onFilteredRowsChange) {
          const hasFilter =
            this.state.textSearch ||
            Object.values(this.state.columnFilters).some(Boolean)
          this.props.onFilteredRowsChange(
            hasFilter ? this.textFilteredRows : undefined
          )
        }
      }
    )
  }
  renderFilter() {
    return (
      <Filter
        onSearch={(textSearch) => {
          this.setState({ textSearch }, () => {
            if (this.props.onFilteredRowsChange) {
              const hasFilter =
                textSearch ||
                Object.values(this.state.columnFilters).some(Boolean)
              this.props.onFilteredRowsChange(
                hasFilter ? this.textFilteredRows : undefined
              )
            }
          })
        }}
      />
    )
  }
  renderCancelButton() {
    return (
      <Stack
        horizontal
        horizontalAlign="start"
        styles={{
          root: {
            padding: 5,
            position: 'absolute',
            transition: 'all 0.3s',
            top: 110,
            right: 20,
          },
        }}
      >
        <DefaultButton
          onClick={() => {
            this.selection.setAllSelected(false)
            if (this.props.onCancel) this.props.onCancel()
          }}
          text={'Cancel'}
          iconProps={{ iconName: 'Cancel' }}
          styles={{ root: { width: 120 } }}
        />
      </Stack>
    )
  }
  renderLoading() {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        verticalFill
        style={{ width: '100%' }}
      >
        <Spinner
          size={SpinnerSize.large}
          labelPosition={'right'}
          label="Loading"
        />
      </Stack>
    )
  }
  getRows(): T[] | undefined {
    const rows = this.props.rows
    if (!rows) {
      return
    }
    if (!rows.length) {
      return rows
    }
    if (this.props.rowToCacheKey(rows[0]) !== this.props.cacheKey) {
      return
    }
    return rows
  }
  getSortedRows(): T[] | undefined {
    const data = this.getRows()
    if (!this.state.sortedColumn || !data) {
      if (data && this.props.postSort) {
        return data.sort((a, b) => this.props.postSort!(a, b, data))
      }
      return data
    }
    const columns = this.getColumns()
    const sortedKey = this.state.sortedColumn!.columnKey
    const sortedDir =
      this.state.sortedColumn!.direction === 'ascending' ? 1 : -1
    const sortedFieldName = columns.find((c) => c.key === sortedKey)!.fieldName!
    const sortableField = (a: any) => {
      let s = a[sortedFieldName]
      if (Array.isArray(s)) {
        s = s
          .map((s) => s.trim())
          .filter(Boolean)
          .join(',')
      }
      if (typeof s === 'string') {
        s = (s as String).toLowerCase()
      }
      return s
    }
    const list = data.slice()
    list
      .sort((a, b) => {
        const aS = sortableField(a)
        const bS = sortableField(b)
        const delta = aS > bS ? 1 : -1
        if (aS == null || aS === '') {
          return sortedDir
        }
        if (bS == null || bS === '') {
          return -sortedDir
        }
        return delta * sortedDir
      })
      .forEach((i, index) => {
        ;(i as any).index = index
      })
    if (this.props.postSort) {
      console.log(list.map((l: any) => [l.id, l.index, l.parentId]))
      list.sort((a, b) => this.props.postSort!(a, b, list))
      console.log(
        'sorted',
        list.map((l: any) => [l.id, l.index, l.sortKey, l.parentId])
      )
    }
    return list
  }
  getColumns(): IColumn[] {
    return this.props.columns.map((menuItem, index) => {
      const className =
        index === this.props.columns.length - 2
          ? 'caroda-before-actions'
          : index === this.props.columns.length - 1
            ? 'caroda-actions'
            : ''
      return {
        ...menuItem,
        isResizable: index > 0 && index < this.props.columns.length - 1,
        isCollapsible: index > 0 && index < this.props.columns.length - 1,
        className: menuItem.className || className,
        headerClassName: menuItem.className || className,
        onColumnClick: (evt, col) => this.sortColumn(evt, col),
        isSorted:
          Boolean(this.state.sortedColumn) &&
          this.state.sortedColumn!.columnKey === menuItem.key,
        isSortedDescending:
          this.state.sortedColumn &&
          this.state.sortedColumn.direction === 'descending' &&
          this.state.sortedColumn.columnKey === menuItem.key,
        onRender: (a: T) => (
          <React.Fragment>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
              }}
              onMouseDown={(e) => {
                this.props.onRowClick(a, e)
              }}
            ></div>
            {menuItem.onRender
              ? menuItem.onRender(a)
              : (a as any)[menuItem.fieldName as string]}
          </React.Fragment>
        ),
      }
    })
  }

  sortColumn(ev: React.MouseEvent<HTMLElement>, column: IColumn) {
    const currentSort = this.state.sortedColumn
    if (!currentSort || currentSort.columnKey !== column.key) {
      this.setState({
        sortedColumn: {
          columnKey: column.key,
          direction: 'ascending',
        },
      })
      return
    }
    if (this.state.sortedColumn!.direction === 'ascending') {
      this.setState({
        sortedColumn: {
          columnKey: column.key,
          direction: 'descending',
        },
      })
      return
    }
    this.setState({ sortedColumn: undefined })
  }
}
